/**
 * Pedidos de venda — a receita (spec 02 §5 · spec 03 §4 · telas 4.1–4.3).
 *
 * Quatro ausências, e cada uma é uma lei:
 *
 * - **Não existe `situacao` de entrada.** Ela é DERIVADA (D-37/DS-21) e viaja só na
 *   saída, pronta da `v_pedido_situacao`. A tela nunca a calcula — o regex que a
 *   4.1 fazia sobre prosa está morto (spec 05 §4).
 * - **Não existe `valor_liquido` nem "valor faturado".** O valor do pedido é o que
 *   se fatura, em qualquer tratamento de comissão (D-75/DS-08).
 * - **Não existe `setor`.** Derivado do Cliente faturado (D-32), só na saída.
 * - **Não existe alteração de vencimento de parcela.** O vencimento é imutável
 *   (DS-11): renegociar cria parcelas novas. Por isso o `PUT /recebiveis` recebe a
 *   agenda inteira só enquanto o pedido está sendo montado.
 */
import { z } from 'zod'
import { COMPORTAMENTOS_DE_VENDA } from '@dv/domain'
import { fracaoDeComissao, chaveDeComando, codigoDeMoeda, dataNominal, uuid } from './comum.js'
import { papelDeParte, tratamentoDeComissao, formaDePagamento, frequenciaDeParcela, MAXIMO_DE_PARCELAS } from './negocios.js'
import { gatilhoDeComissao } from './cadastro.js'
import { janelaDeVeiculacao } from './campanhas.js'

/** Catálogo FECHADO de 8 (D-193/P1-i) (spec 05 §4 · D-95). Os rótulos são os do GLOSSARIO. */
export const situacaoDoPedido = z.enum([
  'Cancelado',
  'Assinatura ativa',
  'A iniciar',
  'Vencido',
  'NF em emissão',
  'A faturar',
  'Faturado',
  'Recebido',
])
export type SituacaoDoPedido = z.infer<typeof situacaoDoPedido>

/** Compromisso é OUTRA coisa que Situação (GLOSSARIO): vive na ficha, não na lista. */
export const compromissoDoPedido = z.enum(['Ativo', 'Encerrado', 'Cancelado'])
export type CompromissoDoPedido = z.infer<typeof compromissoDoPedido>

/** O mesmo enum do cadastro (D-174, DS-618): quando a comissão nasce. Um conceito, um lugar. */
export const gatilhoDaComissao = gatilhoDeComissao

// -------------------------------------------------------------------- saída
// Dinheiro e percentual de comissão vêm null sem pedidos.veValoresFinanceiros.
// A estrutura operacional permanece; null nunca significa quantia zero.

export const partedoPedido = z.object({
  id: uuid,
  papel: papelDeParte,
  empresaId: uuid.nullable(),
  contatoId: uuid.nullable(),
  /**
   * O nome CONGELADO no fechamento (spec 05 §10), com o vivo como reserva.
   *
   * Princípio 10: o que foi vendido não muda porque o cadastro mudou. Uma empresa
   * que troca de razão social não reescreve a nota que já saiu.
   */
  nome: z.string(),
  setor: z.enum(['Público', 'Privado']).nullable(),
  percentual: z.string().nullable(),
  valorComissao: z.string().nullable(),
  tratamentoOverride: tratamentoDeComissao.nullable(),
  gatilhoOverride: gatilhoDaComissao.nullable(),
  tipoComissao: z.enum(['Padrão', 'BV/Cashback']).nullable(),
  regraComissaoId: uuid.nullable(),
  /** O rótulo do perfil de faturamento congelado — só na parte Cliente (D-76). */
  perfilFaturamentoRotulo: z.string().nullable(),
  /**
   * A classificação CONGELADA na comissão desta parte (PK-01): a regra da política que vale
   * — resolvida sozinha ou escolhida à mão —, com a conta contábil e o centro de custo que o
   * pagamento vai usar. Nula fora da parte de parceiro, ou enquanto nenhuma regra se aplica.
   * `default(null)`: quem lê uma resposta sem o campo (dublê antiga, servidor anterior durante o
   * rollout) recebe "sem classificação", e não um erro de contrato.
   */
  classificacaoComissao: z.object({
    regraId: uuid,
    regraNome: z.string(),
    origem: z.enum(['politica', 'manual']),
    contaContabil: z.string().nullable(),
    /**
     * O CÓDIGO da conta, à parte da string "código · nome" (D-136, DS-538). Lido do MESMO retrato
     * congelado, e por isso é o código DA ÉPOCA: depois da renumeração da v1.4.0 (D-164) o `4.1`
     * de um retrato antigo é a Comissões de hoje (3.2), e o `4.1` de hoje é o Pró-labore. Serve para
     * exibir; `default(null)` pela mesma razão do objeto.
     */
    contaContabilCodigo: z.string().nullable().default(null),
    /**
     * O id da conta, do mesmo retrato: é por ele que o link abre o Plano de Contas (DS-604), porque
     * o id não muda quando a conta é renumerada e o código congelado pode ser hoje de outra conta.
     */
    contaContabilId: uuid.nullable().default(null),
    centroCusto: z.string().nullable(),
  }).nullable().default(null),
})
export type PartedoPedido = z.infer<typeof partedoPedido>

/**
 * A prévia da classificação da comissão ANTES de gravar o pedido (PK-01).
 *
 * Uma linha por parceiro, com o tratamento EFETIVO (o do parceiro ou o do pedido). A resposta
 * traz, na mesma ordem, a classificação que a política daria ou o erro nomeado que a gravação
 * devolveria — o formulário não grava o que seria recusado.
 */
export const entradaDaPreviaDaClassificacao = z.object({
  /** O pedido já gravado, na edição: a prévia considera a classificação congelada nele. */
  pedidoId: uuid.nullish(),
  dataVenda: dataNominal,
  clienteEmpresaId: uuid.nullable(),
  parceiros: z.array(z.object({
    empresaId: uuid.nullable(),
    contatoId: uuid.nullable(),
    nome: z.string(),
    tratamento: tratamentoDeComissao,
    regraComissaoId: uuid.nullable(),
  })).max(20),
})
export type EntradaDaPreviaDaClassificacao = z.infer<typeof entradaDaPreviaDaClassificacao>

export const respostaDaPreviaDaClassificacao = z.object({
  parceiros: z.array(z.object({
    classificacao: partedoPedido.shape.classificacaoComissao,
    erro: z.object({ regra: z.string(), mensagem: z.string() }).nullable(),
    /** O acerto que a regra propõe (D-67, D-129): preenche "Como a comissão é acertada?" só se vazio. */
    tratamentoProposto: tratamentoDeComissao.nullable().default(null),
  })),
})
export type RespostaDaPreviaDaClassificacao = z.infer<typeof respostaDaPreviaDaClassificacao>

export const itemDoPedido = z.object({
  id: uuid,
  produtoId: uuid,
  produtoNome: z.string(),
  subprodutoId: uuid.nullable(),
  subprodutoNome: z.string().nullable(),
  /** NOME da forma resolvida, só exibição (D-146): `COALESCE(subproduto, produto).forma_venda_id`. */
  formaVenda: z.string().nullable(),
  /** O COMPORTAMENTO da forma resolvida: é o que decide assinatura, datas e composição (D-146). */
  comportamentoVenda: z.enum(COMPORTAMENTOS_DE_VENDA).nullable(),
  quantidade: z.string().nullable(),
  valor: z.string().nullable(),
  moeda: codigoDeMoeda,
  ordem: z.number().int(),
})
export type ItemDoPedido = z.infer<typeof itemDoPedido>

export const recebivelDoPedido = z.object({
  id: uuid,
  numeroParcela: z.number().int(),
  totalParcelas: z.number().int(),
  vencimento: dataNominal,
  valor: z.string().nullable(),
  moeda: codigoDeMoeda,
  status: z.enum(['pendente', 'nf_emitida', 'recebido', 'cancelado']),
  /**
   * Quanto desta parcela já está coberto por nota VIVA (em emissão ou autorizada).
   *
   * Sem este campo a tela de faturamento não tem como saber o que ainda cabe: `status`
   * responde "tem nota?" e a pergunta certa é "sobrou saldo?" (DS-190). Era o que
   * faltava para o modal deixar de ratear às cegas por todas as parcelas pendentes.
   */
  cobertoPorNf: z.string().nullable(),
  /**
   * O que ainda pode ENTRAR por esta parcela: **coberto − amortizado** (lote F1).
   *
   * É a mesma definição da 4.4 (`recebivel.saldo`), e não `valor − amortizado`: juros e
   * multa entram na conta e não abatem a parcela (D-91), e parcela sem nota não tem o
   * que receber (D-94). "Quanto falta faturar" é outra pergunta, e se responde com
   * `valor − cobertoPorNf`.
   *
   * Vem pronto do servidor, e não subtraído na tela, pela regra da spec 05 §4. Se a
   * ficha calculasse por conta própria, discordaria da 4.4 no dia em que alguém
   * classificasse um juro.
   */
  saldo: z.string().nullable(),
  /*
    A NOTA VIVA da parcela — e por que são três campos e não um.

    Uma parcela pode ser coberta por mais de uma nota (uma parcial e a complementar, uma
    substituição). `nfId` e `numeroNf` só vêm preenchidos quando há EXATAMENTE uma: com
    duas, escolher "a primeira" faria a coluna NF afirmar uma cobertura que não é a
    inteira, e o link levaria a uma das duas sem dizer que havia outra. `notasVivas` é a
    contagem, e é ela que permite à ficha escrever "2 notas".

    São os MESMOS três campos que a 4.4 recebe (`recebiveis.ts`), com o mesmo critério de
    "viva". As duas telas mostram a mesma parcela: discordar aqui seria discordar sobre um
    fato fiscal.
  */
  nfId: uuid.nullable(),
  numeroNf: z.string().nullable(),
  notasVivas: z.number().int(),
  editadaManualmente: z.boolean(),
  /**
   * **Quando esta parcela deve ser faturada** (0131).
   *
   * É a data programada para emitir a nota desta parcela. O combinado com o cliente é o
   * `vencimento`, que quem vende informa e que responde quando se espera RECEBER. A
   * sugestão inicial é `vencimento − prazoDias`, ajustável; depois de gravada a data é um
   * fato: nada a recalcula sozinha, e campanha não a move.
   *
   * `null` é legítimo em parcela histórica anterior à programação e em ciclo que ainda
   * não foi programado: a tela DIZ que não há, não inventa data (DS-99/DS-211).
   */
  faturarEm: dataNominal.nullable(),
})
export type RecebivelDoPedido = z.infer<typeof recebivelDoPedido>

/** A linha da 4.1 — o que cabe na lista sem abrir a ficha. */
export const linhaDePedido = z.object({
  id: uuid,
  codigo: z.string(),
  refExterna: z.string().nullable(),
  titulo: z.string().nullable(),
  /** D-74: Cliente é o nome; Anunciante entre parênteses quando informado e diferente. */
  clienteNome: z.string().nullable(),
  anuncianteNome: z.string().nullable(),
  /**
   * A empresa de cada papel, para o nome da 4.1 abrir a ficha dela (D-136, DS-538). O nome
   * continua sendo o congelado na venda; o id é o do cadastro vivo, e é nulo quando a
   * empresa foi excluída (a ficha responderia 404).
   */
  clienteEmpresaId: uuid.nullable(),
  anuncianteEmpresaId: uuid.nullable(),
  setor: z.enum(['Público', 'Privado']).nullable(),
  dataVenda: dataNominal,
  valor: z.string().nullable(),
  moeda: codigoDeMoeda,
  /** Pronta da view — a tela NÃO deriva nada (spec 05 §4). */
  situacao: situacaoDoPedido,
  compromisso: compromissoDoPedido,
  responsavelNome: z.string().nullable(),
  /** A foto do responsável (PK-04); nula sem responsável ou sem foto. */
  responsavelAvatarAnexoId: uuid.nullable(),
  pracaNome: z.string().nullable(),
  /** Há ao menos um compromisso recorrente, inclusive histórico (spec 4.1). */
  temAssinatura: z.boolean(),
  /** MRR que entra hoje, da mesma view que governa "Assinatura ativa" (D-95). */
  mrrAssinatura: z.string().nullable(),
  /**
   * A janela de veiculação do pedido — a coluna "Veiculação" da 3.2.
   *
   * `null` quando o pedido não tem campanha nenhuma. A coluna DIZ isso; não inventa
   * intervalo nem mostra zero (DS-99/DS-211).
   *
   * Quem responde é o módulo de mídia, por `media/public.ts`: a lista de pedidos nunca
   * lê `campanha`.
   */
  veiculacao: janelaDeVeiculacao.nullable(),
})
export type LinhaDePedido = z.infer<typeof linhaDePedido>

/** Picker de campanhas: saldo e itens completos, com a mesma máscara financeira. */
export const pedidoDisponivelParaCampanha = linhaDePedido.extend({
  saldoNaoAlocado:z.string().nullable(),
  itens:itemDoPedido.array(),
})
export type PedidoDisponivelParaCampanha = z.infer<typeof pedidoDisponivelParaCampanha>

export const pedido = linhaDePedido.extend({
  entityId: uuid,
  negocioId: uuid.nullable(),
  negocioCodigo: z.string().nullable(),
  responsavelId: uuid.nullable(),
  pracaId: uuid.nullable(),
  valorReferencia: z.string().nullable(),
  perfilFaturamentoId: uuid,
  perfilPrazoId: uuid,
  /** O prazo do SNAPSHOT, em dias — não `fn_prazo_empresa` lido de novo. */
  prazoDias: z.number().int().nullable(),
  tratamentoComissao: tratamentoDeComissao.nullable(),
  formaPagamento: formaDePagamento,
  numParcelas: z.number().int().nullable(),
  frequencia: frequenciaDeParcela.nullable(),
  encerradoEm: dataNominal.nullable(),
  motivoEncerramentoNome: z.string().nullable(),
  canceladoEm: dataNominal.nullable(),
  motivoCancelamentoNome: z.string().nullable(),
  partes: z.array(partedoPedido),
  itens: z.array(itemDoPedido.extend({
    /**
     * Alguma campanha usa este item, por composição ou apuração, inclusive a cancelada ou
     * arquivada (D-190, DS-642). Ele não sai do pedido, e dividi-lo em sub-produtos pergunta
     * qual deles continua sendo o item. Só a ficha traz: é ela que a 4.3 edita.
     */
    ligadoACampanha: z.boolean().default(false),
  })),
  recebiveis: z.array(recebivelDoPedido),
  /**
   * Quantas vezes o conjunto de itens foi gravado (D2 · PED-02).
   *
   * O cliente devolve o número que LEU ao gravar; se ele não bate, alguém salvou no
   * meio e a resposta é 409 nomeado, não sobrescrita silenciosa do trabalho alheio.
   */
  versaoItens: z.number().int().nonnegative(),
})
export type Pedido = z.infer<typeof pedido>

// ------------------------------------------------------------------- entrada

/**
 * Uma parcela da agenda, na ENTRADA.
 *
 * `faturarEm` é OPCIONAL, e isso é a fase EXPAND: o cliente antigo não conhece o campo
 * e continua mandando a agenda sem ele — o servidor deriva `vencimento − prazo_dias` do
 * snapshot do pedido e grava. O cliente NOVO manda sempre, porque a partir dele a
 * programação é uma decisão de quem vende, não uma conta do servidor.
 *
 * Quando todo cliente mandar o campo, o `.nullish()` sai por CONTRACT. Enquanto isso, a
 * derivação existe só para o rollout e não depende de campanha nenhuma.
 *
 * Declarado ANTES de `entradaDePedido` porque ela o referencia: esquema Zod é valor em
 * tempo de execução, e a ordem do arquivo é a ordem de avaliação.
 */
export const entradaDeRecebivel = z.object({
  numeroParcela: z.number().int().min(1),
  vencimento: dataNominal,
  valor: z.string(),
  faturarEm: dataNominal.nullish(),
  editadaManualmente: z.boolean().default(false),
})
export type EntradaDeRecebivel = z.input<typeof entradaDeRecebivel>

/**
 * Um item do pedido, na ENTRADA — o mesmo no `POST /pedidos` e no `PUT .../itens`.
 *
 * Declarado ANTES de `entradaDePedido` pelo mesmo motivo de `entradaDeRecebivel`: o
 * `POST` passou a aceitar os itens junto, e esquema Zod é avaliado na ordem do arquivo.
 */
export const entradaDeItemDoPedido = z.object({
  produtoId: uuid,
  subprodutoId: uuid.nullish(),
  quantidade: z.string().nullish(),
  valor: z.string(),
  moeda: codigoDeMoeda.nullish(),
})
export type EntradaDeItemDoPedido = z.input<typeof entradaDeItemDoPedido>

export const entradaDePedido = z.object({
  negocioId: uuid.nullish(),
  responsavelId: uuid.nullish(),
  titulo: z.string().nullish(),
  refExterna: z.string().nullish(),
  pracaId: uuid.nullish(),
  valor: z.string(),
  valorReferencia: z.string().nullish(),
  moeda: codigoDeMoeda.nullish(),
  dataVenda: dataNominal,
  /** D-76: NOT NULL no banco — é o CNPJ que sai na nota. */
  perfilFaturamentoId: uuid,
  /** Snapshot do prazo efetivo; a tela manda o herdado ou o override escolhido. */
  perfilPrazoId: uuid,
  tratamentoComissao: tratamentoDeComissao.nullish(),
  formaPagamento: formaDePagamento.default('unico'),
  numParcelas: z.number().int().min(1).max(MAXIMO_DE_PARCELAS).nullish(),
  frequencia: frequenciaDeParcela.nullish(),
  /**
   * A agenda financeira JUNTO com o pedido — e numa transação só.
   *
   * O fluxo antigo era `POST pedido` e depois `PUT agenda`: duas chamadas, e a janela
   * entre elas é um pedido órfão, sem parcela nenhuma, que fica no banco se a segunda
   * falhar. Mandando a agenda aqui, ou nascem os dois ou não nasce nenhum.
   *
   * Continua OPCIONAL enquanto o cliente antigo existir — sem ela o POST se comporta
   * exatamente como sempre se comportou. Assinatura pura também não a traz: ali a
   * parcela nasce do ciclo, e obrigar agenda imediata seria redesenhar a recorrência.
   */
  recebiveis: entradaDeRecebivel.array().nullish(),
  /**
   * Os itens JUNTO com o pedido e a agenda — e na mesma transação.
   *
   * É o que deixa o servidor recusar a composição mista antes de o pedido existir: sem
   * os itens, o `POST` não sabe se está criando uma assinatura ou uma venda avulsa, e a
   * mistura só aparecia no `PUT .../itens` seguinte, com o pedido e a agenda já gravados.
   *
   * OPCIONAL pelo mesmo motivo da agenda: o cliente antigo cria sem itens e os grava
   * depois pelo `PUT .../itens`, que confere a mesma regra.
   */
  itens: entradaDeItemDoPedido.array().nullish(),
})
export type EntradaDePedido = z.input<typeof entradaDePedido>

/**
 * Edição sem os `.default()` — ver DS-142: `.partial()` não desliga o default.
 *
 * E sem `recebiveis` nem `itens`: a agenda e os itens entram na CRIAÇÃO ou pelos `PUT`
 * próprios, nunca por um PATCH de cabeçalho. Deixá-los no esquema faria o campo ser
 * aceito e ignorado em silêncio, que é o jeito mais barato de alguém achar que gravou.
 */
export const entradaDePartedoPedido = z.object({
  papel: papelDeParte,
  empresaId: uuid.nullish(),
  contatoId: uuid.nullish(),
  percentual: fracaoDeComissao.nullish(),
  valorComissao: z.string().nullish(),
  tratamentoOverride: tratamentoDeComissao.nullish(),
  gatilhoOverride: gatilhoDaComissao.nullish(),
  tipoComissao: z.enum(['Padrão', 'BV/Cashback']).nullish(),
  regraComissaoId: uuid.nullish(),
})
export type EntradaDePartedoPedido = z.input<typeof entradaDePartedoPedido>

export const entradaDeEdicaoDePedido = entradaDePedido
  .omit({ formaPagamento: true, recebiveis: true, itens: true })
  .extend({
    formaPagamento: formaDePagamento,
    /**
     * As partes NA MESMA TRANSAÇÃO do cabeçalho (PK-01). Gravar o cabeçalho e depois as partes
     * materializava a comissão no meio do caminho — data ou tratamento novos com as partes
     * antigas — e recusava o que o estado final aceitaria. Ausente = as partes não mudam.
     */
    partes: z.array(entradaDePartedoPedido),
  })
  .partial()
export type EntradaDeEdicaoDePedido = z.input<typeof entradaDeEdicaoDePedido>

/**
 * O corpo de `POST /pedidos/completo`: o pedido nasce INTEIRO, com as partes, numa transação.
 *
 * O `POST /pedidos` antigo gravava cabeçalho, itens e agenda, e as partes vinham depois por
 * `PUT .../partes`; recusada a segunda, sobrava pedido sem Cliente na fila de faturamento
 * (PV-0003). As partes são obrigatórias aqui, e a rota é outra de propósito: a API anterior
 * descarta campo desconhecido em silêncio, e a mesma rota com `partes` a mais criaria o pedido
 * sem elas. Numa rota que ela não conhece, a resposta é 404 e nada se grava.
 */
export const entradaDeCriacaoDePedido = entradaDePedido.extend({
  partes: z.array(entradaDePartedoPedido).min(1),
})
export type EntradaDeCriacaoDePedido = z.input<typeof entradaDeCriacaoDePedido>

/**
 * O corpo OPCIONAL de `POST /negocios/:id/gerar-pedido` (DS-635).
 *
 * A revisão da 4.3 é o nascimento do pedido: o prazo e o perfil de faturamento que a
 * pessoa VIU congelam ali. Ausentes (o `{}` do cliente N-1, ou sem corpo), valem as
 * reservas de sempre: o perfil do negócio ou o primário do Cliente, e o prazo efetivo do
 * Cliente (`fn_prazo_empresa`). Pedido já gerado: idempotente, o corpo é ignorado e a
 * resposta traz os ids gravados, que a tela confere contra o que mandou.
 */
export const entradaDeGerarPedido = z.object({
  perfilPrazoId: uuid.nullish(),
  perfilFaturamentoId: uuid.nullish(),
})
export type EntradaDeGerarPedido = z.input<typeof entradaDeGerarPedido>


/**
 * O corpo do `PUT /pedidos/:id/itens`.
 *
 * Duas formas aceitas, e isso é a fase EXPAND (D1/D2): o array cru é o que o binário
 * N-1 manda e continua valendo; o envelope acrescenta a chave de idempotência e a
 * versão lida. Quando todo cliente mandar o envelope, o array sai por CONTRACT.
 */
export const entradaDeItemGravadoDoPedido = entradaDeItemDoPedido.extend({
  /**
   * O item GRAVADO que esta linha é (D-190, DS-642): com id, o item é alterado no lugar;
   * sem id, é incluído; o gravado que não vem sai do pedido, se nada o usa. A posição nunca
   * substitui o id: é ele, e só ele, que liga a linha da tela ao item que a campanha usa.
   */
  id: uuid.optional(),
})
export type EntradaDeItemGravadoDoPedido = z.input<typeof entradaDeItemGravadoDoPedido>

export const entradaDeItensDoPedido = z.union([
  entradaDeItemGravadoDoPedido.array(),
  z.object({
    itens: entradaDeItemGravadoDoPedido.array(),
    chaveDeComando: chaveDeComando.optional(),
    /** A versão que o cliente leu. Ausente = "grave por cima" (comportamento antigo). */
    versaoItens: z.number().int().nonnegative().optional(),
    /**
     * A tela identifica cada item gravado pelo id (DS-642). Com ela, um corpo SEM nenhum id
     * quer dizer "todos os itens são novos", e o gravado que ficou de fora sai do pedido (ou
     * é recusado pelo nome, se alguma campanha o usa). Ausente, o corpo sem id é o da tela
     * anterior, que não sabe dos ids: com item usado, só passa a lista idêntica (§6 C2).
     */
    porIdentidade: z.boolean().optional(),
    /** P2-c: confirmação informativa de retirada comercial, preservando as identidades operacionais. */
    confirmaRetiradaDeItensEmCampanha: z.boolean().optional(),
  }),
])
export type EntradaDeItensDoPedido = z.infer<typeof entradaDeItensDoPedido>

/**
 * Renegociação (D-93 · D-98 · DS-22).
 *
 * ## Ela não reescreve nada — cria uma geração nova
 *
 * As parcelas antigas viram `cancelado` apontando a renegociação, as novas nascem
 * apontando a mesma, e a ficha mostra as DUAS gerações. É a contraparte do vencimento
 * imutável (DS-11): a data combinada antes continua legível depois, que é o que se
 * precisa mostrar ao cliente numa discussão.
 *
 * ## Os encargos entram nas parcelas novas (D-93), e NÃO são faturados (D-98)
 *
 * D117: a soma nova é o principal restante da agenda vigente mais os encargos
 * anteriores pendentes e os informados agora. Nenhum documento fiscal
 * nasce por causa do encargo — a nota velha continua válida e cobre o principal.
 * O fundador foi literal: *"não vou cancelar uma NF para emitir outra com poucos reais
 * de juros"*.
 */
export const entradaDeRenegociacao = z.object({
  motivo: z.string().min(1),
  /** Cobrança financeira nascida do atraso. Ausente quando a renegociação é só de prazo. */
  encargos: z.string().nullable().optional(),
  parcelas: z
    .array(
      z.object({
        numeroParcela: z.number().int().min(1),
        vencimento: dataNominal,
        valor: z.string(),
      }),
    )
    .min(1),
})
export type EntradaDeRenegociacao = z.input<typeof entradaDeRenegociacao>

/** DS-34/DS-46: cancelar EXIGE motivo do catálogo — o texto livre não substitui. */
export const entradaDeCancelamento = z.object({ motivoId: uuid })
export const entradaDeEncerramento = z.object({
  motivoId: uuid,
  observacao: z.string().nullish(),
})
export type EntradaDeCancelamento = z.input<typeof entradaDeCancelamento>
export type EntradaDeEncerramento = z.input<typeof entradaDeEncerramento>

/** D117: posição atual usada para propor um novo acordo. POST revalida sob locks. */
export const contextoDeRenegociacao = z.object({
  principalRestante:z.string(),encargosPendentes:z.string(),saldoRestante:z.string(),moeda:codigoDeMoeda,
  podeRenegociar:z.boolean(),motivoBloqueio:z.string().nullable(),
})
export type ContextoDeRenegociacao=z.infer<typeof contextoDeRenegociacao>
